/* eslint-disable react/jsx-props-no-spreading, react/no-danger */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { injectIntl, FormattedMessage } from 'react-intl';
import Reaptcha from 'reaptcha';

import { config as appConfig } from '@xingternal/hops';
import { StatusBanner } from '@xingternal/banner';
import Grid, { Column, Row } from '@xingternal/xing-grid';
import { Headline } from '@xingternal/typography';
import { FormField, Select, Option } from '@xingternal/text-field';
import { Button } from '@xingternal/button';
import TextSubject from './TextSubject';
import LinkSubject from './LinkSubject';
import FileUpload from './FileUpload/FileUpload';
import { relocateTo } from '../state/helpers';

import styles from './contact.css';

const { recaptchaSitekey } = appConfig;

class ContactForm extends Component {
  constructor(props) {
    super(props);
    this.recaptchaInstance = null;
    this.state = {
      isUploadingFile: false,
    };
  }

  componentDidMount() {
    const {
      fields: { topic, subject },
      config,
      updateLocation,
    } = this.props;
    const relocation = relocateTo(topic, subject, config);

    if (topic !== relocation.topic || subject !== relocation.subject) {
      updateLocation(relocation, { force: true });
    }
  }

  handleChange = (inputName) => (event) => {
    const { updateFields } = this.props;
    updateFields({ [inputName]: event.target.value });
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const { submitForm, fields } = this.props;
    submitForm(fields);
    if (this.recaptchaInstance) this.recaptchaInstance.reset();
  };

  handleRelocate = (inputName) => (event) => {
    const { config, fields, updateLocation } = this.props;
    const { topic, subject } = {
      ...fields,
      [inputName]: event.target.value,
    };

    const relocation = relocateTo(topic, subject, config);
    updateLocation(relocation);
  };

  handleVerify = (token = '') => {
    const { updateFields } = this.props;
    updateFields({ recaptcha: token });
  };

  render() {
    const {
      intl: { locale, formatMessage },
      addFile,
      removeFile,
      fields,
      errors,
      submitError,
      submitting,
      user,
      topics,
      subjects,
      subjectForm,
      showSubmit,
      showUpload,
      submitDisabled,
    } = this.props;

    const { isUploadingFile } = this.state;

    return (
      <Grid type="standard">
        <Row>
          <Column size={12}>
            <form onSubmit={this.handleSubmit} className={styles.contactForm}>
              <Headline size="xlarge">
                <FormattedMessage id="SUPPORTHTML_SUB0" />
              </Headline>
              <div
                dangerouslySetInnerHTML={{
                  __html: formatMessage({
                    id: 'SUPPORTHTML_HELP_DESCRIPTION',
                  }),
                }}
              />
              <StatusBanner display="inline" variant="error" show={submitError}>
                <FormattedMessage id="ERROR_SOMETHING_WENT_WRONG" />
              </StatusBanner>
              <div>
                {!user.email && (
                  <FormField
                    name="email"
                    value={fields.email}
                    className={styles.formInput}
                    label={<FormattedMessage id="SUPPORTHTML_MAIL" />}
                    onChange={this.handleChange('email')}
                    error={errors.email}
                    disabled={submitting}
                    medium
                  />
                )}

                {!user.name && (
                  <FormField
                    name="name"
                    value={fields.name}
                    className={styles.formInput}
                    label={<FormattedMessage id="SUPPORTHTML_SENDNAME" />}
                    onChange={this.handleChange('name')}
                    message={errors.name}
                    disabled={submitting}
                    medium
                  />
                )}

                <Select
                  name="topic"
                  value={fields.topic}
                  className={styles.formInput}
                  placeholder={formatMessage({ id: 'CONTACT_FORM_CHOOSE_TOPIC' })}
                  onChange={this.handleRelocate('topic')}
                  error={errors.topic}
                  disabled={submitting}
                  helperText="&nbsp;"
                  medium
                >
                  {topics.map((topic) => (
                    <Option key={topic.name} value={topic.name}>
                      {topic.label}
                    </Option>
                  ))}
                </Select>

                <Select
                  name="subject"
                  value={fields.subject}
                  className={cx(styles.formInput, styles.formInputSubject)}
                  placeholder={formatMessage({ id: 'CONTACT_FORM_CHOOSE_SUBJECT' })}
                  onChange={this.handleRelocate('subject')}
                  error={errors.subject}
                  disabled={submitting || !fields.topic}
                  helperText="&nbsp;"
                  medium
                >
                  {subjects.map((subject) => (
                    <Option key={subject.name} value={subject.name}>
                      {subject.label}
                    </Option>
                  ))}
                </Select>

                {subjectForm.type === 'text' && (
                  <TextSubject
                    value={fields.message}
                    placeholder={subjectForm.placeholder}
                    errors={errors}
                    submitting={submitting}
                    handleChange={this.handleChange}
                  />
                )}

                {subjectForm.type === 'link' && <LinkSubject {...subjectForm} />}
              </div>

              <div className={showSubmit ? styles.file_upload : styles.file_upload_hidden}>
                <div>
                  <FormattedMessage id="CONTACT_FORM_FILE_UPLOAD_HEADLINE" />
                  {!showUpload && (
                    <span className={styles.uploadUnavailableMessage}>
                      <FormattedMessage id="CONTACT_FORM_FILE_UPLOAD_UNAVAILABLE" />
                    </span>
                  )}
                </div>
                {showUpload && (
                  <FileUpload
                    onFileUploaded={addFile}
                    onFileRemoved={removeFile}
                    onStatusChange={({ isUploading }) =>
                      this.setState({ isUploadingFile: isUploading })
                    }
                    error={errors.files}
                  />
                )}
              </div>

              {subjectForm.type !== 'link' && (
                <div>
                  {user.email && showSubmit && (
                    <StatusBanner
                      className={styles.explanation}
                      display="inline"
                      variant="info"
                      show={showSubmit}
                    >
                      <FormattedMessage
                        id="CONTACT_FORM_USER_EMAIL_INFO"
                        tagName="span"
                        values={{
                          email: user.email,
                          strong: (content) => <strong>{content}</strong>,
                          a: (content) => (
                            <a href="/settings/account/credentials/email">{content}</a>
                          ),
                        }}
                      />
                    </StatusBanner>
                  )}
                </div>
              )}

              <div className={showSubmit ? styles.submission : styles.submission_hidden}>
                <Row>
                  {!user.email && (
                    <Column size={12} sizeDesktop={9}>
                      <Reaptcha
                        ref={(e) => {
                          this.recaptchaInstance = e;
                        }}
                        className={styles.recaptcha}
                        sitekey={recaptchaSitekey}
                        render="explicit"
                        hl={locale}
                        onVerify={this.handleVerify}
                        onExpire={this.handleVerify}
                      />
                    </Column>
                  )}
                  <Column size={12} sizeDesktop={3}>
                    <Button
                      id="sp-submit-button"
                      className={user.email ? styles.submitButton : ''}
                      variant="primary"
                      size="medium"
                      type="submit"
                      loading={submitting}
                      disabled={submitDisabled || isUploadingFile}
                    >
                      <FormattedMessage id="BUTTON_SEND" />
                    </Button>
                  </Column>
                </Row>
              </div>
            </form>

            <div
              dangerouslySetInnerHTML={{
                __html: formatMessage({
                  id: 'SUPPORTHTML_HELP_DESCRIPTION_ONLYFY',
                }),
              }}
            />
          </Column>
        </Row>
      </Grid>
    );
  }
}

ContactForm.defaultProps = {
  fields: {
    email: '',
    name: '',
    message: '',
    topic: '',
    subject: '',
    recaptcha: '',
  },
  errors: {},
  submitDisabled: false,
  submitError: false,
  submitting: false,
  topics: [],
  subjects: [],
  subjectForm: {},
  showSubmit: false,
  showUpload: false,
  addFile() {},
  removeFile() {},
  submitForm() {},
  updateFields() {},
  updateLocation() {},
};

ContactForm.propTypes = {
  config: PropTypes.shape({
    topics: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    subject: PropTypes.string,
    topic: PropTypes.string,
    files: PropTypes.string,
  }),
  fields: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    message: PropTypes.string,
    topic: PropTypes.string,
    subject: PropTypes.string,
    recaptcha: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.string),
  }),
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
    formatMessage: PropTypes.func,
  }).isRequired,
  subjects: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  showSubmit: PropTypes.bool,
  showUpload: PropTypes.bool,
  subjectForm: PropTypes.shape({
    type: PropTypes.oneOf(['text', 'link', '']),
    placeholder: PropTypes.string,
  }),
  submitting: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  submitError: PropTypes.bool,
  submitForm: PropTypes.func,
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  updateLocation: PropTypes.func,
  updateFields: PropTypes.func,
  addFile: PropTypes.func,
  removeFile: PropTypes.func,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
};

export default injectIntl(ContactForm);
